import { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'next-i18next';

import Carousel, { useCarousel } from '_components/Carousel';
import { Breakpoints } from '@hooks/useBreakpoint';
import type { ImageProductStripProduct } from '@adapters/prismic/transforms/imageProductStrip';
import ProductTile from '_components/ProductTile';

import styles from './ProductStrip.module.scss';

export interface ProductStripProps {
  products: ImageProductStripProduct[];
  header: ReactNode;
  interactionLocation: string;
  tabName: string;
  coOpBrand: string | null;
}

const ProductStrip = (props: ProductStripProps): ReactElement => (
  <Carousel.Provider>
    <ProductStripInner {...props} />
  </Carousel.Provider>
);

const ProductStripInner = ({
  products,
  header,
  interactionLocation,
  tabName,
  coOpBrand,
}: ProductStripProps): ReactElement => {
  const { t } = useTranslation('common');
  const {
    state: { isScrollable },
  } = useCarousel();

  return (
    <section
      aria-label={
        products.length > 0 ? t('imageProductStrip.ariaLabel') : undefined
      }
      className={styles.productStrip}
    >
      <div className={styles.headerRow}>
        {header}
        {products.length > 0 && isScrollable && (
          <div className={styles.nav}>
            <Carousel.NavPrev
              aria-label={t('productStrip.navPrev')}
              enabledType="secondary"
              disabledType="outline"
              className={styles.navButton}
            />
            <Carousel.NavNext
              aria-label={t('productStrip.navNext')}
              enabledType="secondary"
              disabledType="outline"
              className={styles.navButton}
            />
          </div>
        )}
      </div>

      {products.length > 0 && (
        <div className={styles.trackWrapper}>
          <Carousel.Track
            wrapperTag="ul"
            className={styles.track}
            spaceBetween={4}
            breakpoints={{ [Breakpoints.md]: { spaceBetween: 8 } }}
          >
            {products.map(({ product, variantSku }, index) => (
              <Carousel.Slide
                tag="li"
                key={product.parentSku}
                className={styles.slide}
              >
                <ProductTile
                  product={product}
                  variantSku={variantSku}
                  interactionLocation={interactionLocation}
                  tabName={tabName}
                  coOpBrand={coOpBrand}
                  index={index}
                  className={styles.card}
                />
              </Carousel.Slide>
            ))}
          </Carousel.Track>
        </div>
      )}
    </section>
  );
};

export default ProductStrip;
